import { useState, useEffect, useCallback } from 'react'

interface ServiceWorkerState {
  needRefresh: boolean
  updateServiceWorker: () => Promise<void>
}

export function useServiceWorker(): ServiceWorkerState {
  const [needRefresh, setNeedRefresh] = useState(false)
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null)
  const [, setRegistration] = useState<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    let reg: ServiceWorkerRegistration | null = null

    const registerSW = async () => {
      try {
        reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
        setRegistration(reg)

        // Check if there's already a waiting worker
        if (reg.waiting) {
          setWaitingWorker(reg.waiting)
          setNeedRefresh(true)
        }

        // Listen for new service worker installing
        reg.addEventListener('updatefound', () => {
          const newWorker = reg!.installing
          if (!newWorker) return

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New SW installed, waiting to activate
              setWaitingWorker(newWorker)
              setNeedRefresh(true)
            }
          })
        })
      } catch (error) {
        console.error('[SW] Registration failed:', error)
      }
    }

    registerSW()

    // Periodically check for updates (every 60 minutes)
    const interval = setInterval(() => {
      reg?.update().catch(() => {})
    }, 60 * 60 * 1000)

    // Check for updates on visibility change
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reg?.update().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Reload when new SW takes control (after skipWaiting)
    let refreshing = false
    const onControllerChange = () => {
      if (refreshing) return
      refreshing = true
      window.location.reload()
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
  }, [])

  const updateServiceWorker = useCallback(async () => {
    if (!waitingWorker) return
    // Tell waiting SW to skip waiting and take over
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  }, [waitingWorker])

  return { needRefresh, updateServiceWorker }
}
